import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Trash2, Copy, Code2, Shield, Plus } from "lucide-react";
import { ShareGateControls } from "@/components/bi/ShareGateControls";
import { DashboardGroupGrants, DashboardCategoryField, GateToggles } from "@/components/bi/AccessGrants";
import { NUM_RANGE_PRESETS, TIER_LEVELS } from "@/lib/filterPanel";

// ---------- Manage Access ----------
const DASH_ROLES = [
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
  { value: "publisher", label: "Publisher" },
];
const USER_LEVELS = [{ value: "none", label: "No access" }, ...DASH_ROLES];

export function AccessDialog({ open, onOpenChange, dashboardId, onOwnerChanged = null, onCategoryChanged = null }) {
  const [collabs, setCollabs] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [groupGrants, setGroupGrants] = useState([]);
  const [category, setCategory] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/dashboards/${dashboardId}/access`);
      setCollabs(data.collaborators || []); setOwnerEmail(data.owner_email || "");
      setGroupGrants(data.group_grants || []); setCategory(data.category || "");
    }
    catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    try { const { data } = await api.post(`/dashboards/${dashboardId}/access`, { email: email.trim(), role }); setCollabs(data.collaborators); setEmail(""); toast.success(role === "none" ? "Access explicitly blocked" : "Access granted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (uid) => {
    try { const { data } = await api.delete(`/dashboards/${dashboardId}/access/${uid}`); setCollabs(data.collaborators); }
    catch { toast.error("Failed"); }
  };
  const patchCollab = async (uid, patch, msg = "Access updated") => {
    try { const { data } = await api.patch(`/dashboards/${dashboardId}/access/${uid}`, patch); setCollabs(data.collaborators); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeRole = (uid, newRole) => patchCollab(uid, { role: newRole }, "Role updated");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [keepAs, setKeepAs] = useState("publisher");
  const transfer = async () => {
    if (!transferEmail.trim()) { toast.error("Enter the new owner's email"); return; }
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/transfer-owner`, { email: transferEmail.trim(), keep_previous_owner_as: keepAs });
      setOwnerEmail(data.owner_email); setCollabs(data.collaborators || []); setTransferOpen(false); setTransferEmail("");
      toast.success(`Ownership transferred to ${data.owner_email}`);
      onOwnerChanged && onOwnerChanged(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Shield className="w-5 h-5 text-[color:var(--brand-ocean)]" />Manage access</DialogTitle></DialogHeader>
        <p className="text-sm text-[color:var(--ink-3)]">Grant users view, edit or publish access — or block someone explicitly with “No access”. Users must exist in your Team first. Viewing data also requires access to the dashboard's datasets.</p>
        <div className="flex gap-2 mt-2">
          <Input data-testid="access-email" placeholder="user@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[color:var(--surface)]" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="access-role" className="w-[128px] shrink-0 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>{USER_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="access-add" onClick={add} className="bg-[color:var(--brand-ocean)] text-white shrink-0"><UserPlus className="w-4 h-4" /></Button>
        </div>
        <div className="mt-3 space-y-2 max-h-60 overflow-auto">
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-[color:var(--surface-2)]">
            <span className="text-sm text-[color:var(--ink-2)]" data-testid="access-owner-email">{ownerEmail}</span>
            <span className="flex items-center gap-2">
              <Badge className="bg-[color:rgba(18,132,174,0.15)] text-[color:var(--brand-ocean)] border-0">Owner</Badge>
              <button data-testid="access-transfer-btn" onClick={() => setTransferOpen((v) => !v)} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline">Transfer…</button>
            </span>
          </div>
          {transferOpen && (
            <div data-testid="access-transfer-panel" className="rounded-lg border border-[color:var(--glass-border)] p-2 space-y-2">
              <p className="text-[11px] text-[color:var(--ink-3)]">The new owner gets full control. You choose what the previous owner keeps.</p>
              <div className="flex gap-2">
                <Input data-testid="access-transfer-email" placeholder="new-owner@email.com" value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)} className="bg-[color:var(--surface)] h-8 text-xs" />
                <Select value={keepAs} onValueChange={setKeepAs}>
                  <SelectTrigger data-testid="access-transfer-keep" className="w-[150px] shrink-0 h-8 text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DASH_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>Previous owner → {r.label}</SelectItem>)}
                    <SelectItem value="none">Previous owner → no access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setTransferOpen(false)}>Cancel</Button>
                <Button data-testid="access-transfer-confirm" size="sm" className="h-7 text-xs bg-[color:var(--brand-ocean)] text-white" onClick={transfer}>Transfer ownership</Button>
              </div>
            </div>
          )}
          {collabs.map((c) => (
            <div key={c.user_id} data-testid="access-collab-row" className={`flex items-center justify-between px-3 py-2 rounded-lg border ${c.role === "none" ? "border-[color:var(--danger)]/40 bg-[color:rgba(232,93,106,0.05)]" : "border-[color:var(--glass-border)]"}`}>
              <span className="text-sm text-[color:var(--ink-2)] truncate flex items-center gap-1.5">{c.email}{c.role === "none" && <Badge className="bg-[color:rgba(232,93,106,0.12)] text-[color:var(--danger)] border-0 text-[9px] px-1 py-0">blocked</Badge>}</span>
              <div className="flex items-center gap-2 shrink-0">
                <GateToggles gates={c.gates} level={c.role || "viewer"} testid={`access-collab-gate-${c.user_id}`} onChange={(gates) => patchCollab(c.user_id, { gates }, "Feature gates updated")} />
                <Select value={c.role || "viewer"} onValueChange={(v) => changeRole(c.user_id, v)}>
                  <SelectTrigger data-testid="access-collab-role" className="h-7 w-[112px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{USER_LEVELS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid="access-collab-remove" onClick={() => remove(c.user_id)} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <DashboardGroupGrants dashboardId={dashboardId} grants={groupGrants} onChange={setGroupGrants} />
        <DashboardCategoryField dashboardId={dashboardId} category={category} onChange={(c) => { setCategory(c); onCategoryChanged && onCategoryChanged(c); }} />
      </DialogContent>
    </Dialog>
  );
}

// ---------- Embed ----------
export function EmbedDialog({ open, onOpenChange, dashboard, onUpdate }) {
  const [token, setToken] = useState(dashboard?.embed_token || null);
  const [enabled, setEnabled] = useState(!!dashboard?.embed_enabled);
  useEffect(() => { setToken(dashboard?.embed_token || null); setEnabled(!!dashboard?.embed_enabled); }, [dashboard]);

  const enable = async () => {
    try { const { data } = await api.post(`/dashboards/${dashboard.id}/embed`); setToken(data.embed_token); setEnabled(true); onUpdate && onUpdate({ embed_enabled: true, embed_token: data.embed_token }); }
    catch { toast.error("Failed"); }
  };
  const disable = async () => {
    try { await api.post(`/dashboards/${dashboard.id}/embed/disable`); setEnabled(false); onUpdate && onUpdate({ embed_enabled: false }); }
    catch { toast.error("Failed"); }
  };

  const [embOpts, setEmbOpts] = useState({ hideFilters: false, noInteract: false, transparent: false });
  const optParams = [embOpts.hideFilters && "hideFilters=1", embOpts.noInteract && "noInteract=1", embOpts.transparent && "transparent=1"].filter(Boolean).join("&");
  const embedUrl = token ? `${window.location.origin}/embed/${token}` : "";
  const embedUrlFull = optParams ? `${embedUrl}?${optParams}` : embedUrl;
  const iframe = `<iframe src="${embedUrlFull}" width="100%" height="600" frameborder="0" style="border:0;border-radius:16px;"></iframe>`;
  const backendBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const sdkSnippet = `<script src="${backendBase}/api/public/embed-sdk.js"></script>
<div id="my-dashboard"></div>
<script>
  var dash = EazyBI.embed({
    container: "#my-dashboard",
    url: "${embedUrl}",
    autoHeight: true,${optParams ? `\n    options: { ${[embOpts.hideFilters && "hideFilters: true", embOpts.noInteract && "noInteract: true", embOpts.transparent && "transparent: true"].filter(Boolean).join(", ")} },` : ""}
    filters: { /* region: "North" */ },
  });
  dash.on("loaded", function (e) { console.log("dashboard ready", e.dashboard); });
  dash.on("filterChanged", function (e) { console.log("filters", e.values); });
  // dash.applyFilters({ region: "South" }); dash.refresh(); dash.destroy();
</script>`;
  const copy = (t, msg) => { navigator.clipboard.writeText(t); toast.success(msg); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Code2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />Embed dashboard</DialogTitle></DialogHeader>
        {!enabled ? (
          <>
            <p className="text-sm text-[color:var(--ink-3)]">Generate a secure, tokenized link to embed this dashboard in any website via an iframe.</p>
            <Button data-testid="embed-enable" onClick={enable} className="bg-[color:var(--brand-ocean)] text-white mt-2">Generate secure embed link</Button>
          </>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Display options</Label>
              <div className="flex flex-wrap gap-3 mt-1.5">
                <label className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]"><Checkbox data-testid="embed-opt-hidefilters" checked={embOpts.hideFilters} onCheckedChange={(v) => setEmbOpts((s) => ({ ...s, hideFilters: !!v }))} />Hide filter bar</label>
                <label className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]"><Checkbox data-testid="embed-opt-nointeract" checked={embOpts.noInteract} onCheckedChange={(v) => setEmbOpts((s) => ({ ...s, noInteract: !!v }))} />Disable cross-filtering</label>
                <label className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]"><Checkbox data-testid="embed-opt-transparent" checked={embOpts.transparent} onCheckedChange={(v) => setEmbOpts((s) => ({ ...s, transparent: !!v }))} />Transparent background</label>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Embed URL (secure token)</Label>
              <div className="flex gap-2 mt-1.5"><Input data-testid="embed-url" readOnly value={embedUrlFull} className="bg-[color:var(--surface)] text-xs" /><Button onClick={() => copy(embedUrlFull, "URL copied")} variant="outline" className="bg-[color:var(--surface)]"><Copy className="w-4 h-4" /></Button></div>
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">iframe snippet</Label>
              <div className="flex gap-2 mt-1.5"><textarea data-testid="embed-iframe" readOnly value={iframe} className="flex-1 bg-[color:var(--surface)] text-xs font-mono rounded-lg border border-[color:var(--glass-border)] p-2 h-20" /><Button onClick={() => copy(iframe, "Snippet copied")} variant="outline" className="bg-[color:var(--surface)]"><Copy className="w-4 h-4" /></Button></div>
            </div>
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">JavaScript SDK (auto-height, events, programmatic filters)</Label>
              <div className="flex gap-2 mt-1.5"><textarea data-testid="embed-sdk" readOnly value={sdkSnippet} className="flex-1 bg-[color:var(--surface)] text-[10px] font-mono rounded-lg border border-[color:var(--glass-border)] p-2 h-40" /><Button onClick={() => copy(sdkSnippet, "SDK snippet copied")} variant="outline" className="bg-[color:var(--surface)]"><Copy className="w-4 h-4" /></Button></div>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">SDK methods: <b>applyFilters, clearFilters, refresh, getFilters, resize, destroy</b>. Events: <b>loaded, height, filterChanged, filters</b>.</p>
            </div>
            <div className="bg-[color:var(--surface-2)] rounded-lg p-3">
              <Label className="text-xs font-medium text-[color:var(--ink-2)]">Parameterized embeds</Label>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Pre-apply filters by adding query params. Example:</p>
              <code className="block text-[11px] font-mono text-[color:var(--brand-ocean)] mt-1 break-all">{embedUrl}?f_region=North&f_category=A,B</code>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Use <b>f_&lt;field&gt;=value</b> (comma-separated for multiple values).</p>
            </div>
            <Button data-testid="embed-disable" variant="ghost" onClick={disable} className="text-[color:var(--danger)]">Disable embedding</Button>
            <ShareGateControls dashboardId={dashboard.id} scope="embed" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Add global filter ----------
export function AddFilterDialog({ open, onOpenChange, datasets, onAdd, existingFilters = [], tabs = [], activeTabId = "",
  filterLayout = "bar", onFilterLayoutChange }) {
  const [datasetId, setDatasetId] = useState("");
  const [field, setField] = useState("");
  const [type, setType] = useState("select");
  const [label, setLabel] = useState("");
  const [parentId, setParentId] = useState("");
  const [defVal, setDefVal] = useState("");
  const [required, setRequired] = useState(false);
  const [locked, setLocked] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [fiscalStart, setFiscalStart] = useState("1");
  // Item 98 \u2014 FilterPanel options
  const [searchFields, setSearchFields] = useState([]);
  const [topN, setTopN] = useState("8");
  const [sort, setSort] = useState("count");
  const [counts, setCounts] = useState(true);
  const [exclude, setExclude] = useState(true);
  const [showBlank, setShowBlank] = useState(true);
  const [toggleOp, setToggleOp] = useState("eq");
  const [toggleVal, setToggleVal] = useState("");
  const [scope, setScope] = useState("dashboard");   // item 97: dashboard-wide or this tab only
  const ds = datasets.find((d) => d.id === datasetId);
  const fields = ds?.schema || [];
  const parentOptions = existingFilters.filter((f) => f.dataset_id === datasetId && f.type !== "daterange");
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const DATE_DEFAULTS = ["last_7", "last_30", "last_90", "this_month", "last_month", "this_quarter", "this_year", "this_fiscal_year"];

  const reset = () => {
    setDatasetId(""); setField(""); setType("select"); setLabel(""); setParentId(""); setDefVal("");
    setRequired(false); setLocked(false); setHidden(false); setFiscalStart("1");
    setSearchFields([]); setTopN("8"); setSort("count"); setCounts(true); setExclude(true); setShowBlank(true);
    setToggleOp("eq"); setToggleVal(""); setScope("dashboard");
  };
  const submit = () => {
    if (!datasetId) { toast.error("Pick a dataset"); return; }
    if (type === "search" ? !searchFields.length : !field) {
      toast.error(type === "search" ? "Pick at least one field to search" : "Pick a dataset and field");
      return;
    }
    onAdd({
      id: `gf_${Date.now()}`, dataset_id: datasetId, field: type === "search" ? searchFields[0] : field,
      type, label: label || (type === "search" ? "Search" : field), parent_id: parentId || null,
      default: defVal || null, required, locked, hidden,
      ...(scope === "tab" && activeTabId ? { tab_id: activeTabId } : {}),
      ...(type === "daterange" ? { fiscal_start: Number(fiscalStart) || 1 } : {}),
      ...(type === "search" ? { fields: searchFields, placeholder: label ? `Search ${label}` : "" } : {}),
      ...(type === "toggle" ? { op: toggleOp, value: toggleVal === "" ? true : toggleVal } : {}),
      ...(type === "numrange" ? { ranges: NUM_RANGE_PRESETS } : {}),
      ...(type === "tier" ? { levels: TIER_LEVELS, icon: "star" } : {}),
      ...(["checkbox", "multiselect", "select"].includes(type)
        ? { counts, top_n: Number(topN) || 8, sort, exclude, show_blank: showBlank }
        : {}),
    });
    reset(); onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[86vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Plus className="w-5 h-5 text-[color:var(--brand-ocean)]" />Add dashboard filter</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          {onFilterLayoutChange && (
            <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5">
              <Label className="text-xs">Where these filters appear</Label>
              <Select value={filterLayout} onValueChange={onFilterLayoutChange}>
                <SelectTrigger data-testid="addfilter-layout" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Top bar (default)</SelectItem>
                  <SelectItem value="panel">Side panel with live counts</SelectItem>
                  <SelectItem value="both">Both — panel plus the top bar</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Applies to every filter on this dashboard. The panel shows result counts next to each option and works for viewers too.</p>
            </div>
          )}
          <div><Label>Dataset</Label>
            <Select value={datasetId} onValueChange={(v) => { setDatasetId(v); setField(""); }}>
              <SelectTrigger data-testid="addfilter-dataset" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Select dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {type !== "search" && (
          <div><Label>Field</Label>
            <Select value={field} onValueChange={setField}>
              <SelectTrigger data-testid="addfilter-field" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Select field" /></SelectTrigger>
              <SelectContent>{fields.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} <span className="text-[color:var(--ink-3)]">({c.type})</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          )}
          <div><Label>Control type</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setDefVal(""); }}>
              <SelectTrigger data-testid="addfilter-type" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="select">Single select</SelectItem>
                <SelectItem value="multiselect">Multi select (searchable)</SelectItem>
                <SelectItem value="checkbox">Checkbox list with live counts</SelectItem>
                <SelectItem value="daterange">Date range</SelectItem>
                <SelectItem value="numrange">Numeric range (with presets)</SelectItem>
                <SelectItem value="tier">Rating / tier threshold</SelectItem>
                <SelectItem value="toggle">Toggle (on / off)</SelectItem>
                <SelectItem value="search">Global search (several fields)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === "search" && (
            <div><Label>Fields to search</Label>
              <div data-testid="addfilter-search-fields" className="mt-1.5 max-h-[160px] overflow-y-auto rounded-xl border border-[color:var(--glass-border)] p-2 space-y-1">
                {fields.length === 0 && <p className="text-[11px] text-[color:var(--ink-3)]">Pick a dataset first.</p>}
                {fields.map((c) => (
                  <label key={c.name} className="flex items-center gap-2 text-xs text-[color:var(--ink-2)] cursor-pointer">
                    <Switch className="scale-75" checked={searchFields.includes(c.name)}
                      onCheckedChange={(v) => setSearchFields((list) => (v ? [...list, c.name] : list.filter((x) => x !== c.name)))} />
                    {c.name}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">One box searches all of these columns at once (case-insensitive, partial match).</p>
            </div>
          )}
          {type === "toggle" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Condition</Label>
                <Select value={toggleOp} onValueChange={setToggleOp}>
                  <SelectTrigger data-testid="addfilter-toggle-op" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["eq", "neq", "gt", "gte", "lt", "lte", "contains"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Value when on</Label>
                <Input data-testid="addfilter-toggle-value" value={toggleVal} onChange={(e) => setToggleVal(e.target.value)} placeholder="e.g. Yes / 1 / true" className="mt-1.5 bg-[color:var(--surface)]" />
              </div>
            </div>
          )}
          {["checkbox", "multiselect", "select"].includes(type) && (
            <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2.5">
              <div className="text-xs font-medium text-[color:var(--ink-2)]">Filter panel options</div>
              <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Show live result counts</Label><Switch data-testid="addfilter-counts" checked={counts} onCheckedChange={setCounts} /></div>
              <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Offer “exclude these”</Label><Switch data-testid="addfilter-exclude" checked={exclude} onCheckedChange={setExclude} /></div>
              <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Offer blank / empty values</Label><Switch data-testid="addfilter-blank" checked={showBlank} onCheckedChange={setShowBlank} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[color:var(--ink-2)]">Options before “Show all”</Label>
                  <Input data-testid="addfilter-topn" type="number" min={3} max={50} value={topN} onChange={(e) => setTopN(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" />
                </div>
                <div><Label className="text-xs text-[color:var(--ink-2)]">Order options by</Label>
                  <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger data-testid="addfilter-sort" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="count">Most results first</SelectItem><SelectItem value="alpha">A → Z</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          {tabs.length > 1 && (
            <div><Label>Applies to</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="addfilter-scope" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dashboard">Every tab (dashboard filter)</SelectItem>
                  <SelectItem value="tab">Only this tab — {(tabs.find((t) => t.id === activeTabId) || {}).name || "current"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {type === "daterange" && (
            <div><Label>Fiscal year starts in</Label>
              <Select value={fiscalStart} onValueChange={setFiscalStart}>
                <SelectTrigger data-testid="addfilter-fiscal" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Used by the “This/Last fiscal year” presets.</p>
            </div>
          )}
          {type !== "daterange" && parentOptions.length > 0 && (
            <div><Label>Linked to parent filter (optional)</Label>
              <Select value={parentId || "__none__"} onValueChange={(v) => setParentId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="addfilter-parent" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (auto-cascade with all filters)</SelectItem>
                  {parentOptions.map((f) => <SelectItem key={f.id} value={f.id}>{f.label || f.field}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Options in this filter will be narrowed to the parent's selection (e.g. Country → State → Region).</p>
            </div>
          )}
          <div><Label>Default value (optional)</Label>
            {type === "daterange" ? (
              <Select value={defVal || "__none__"} onValueChange={(v) => setDefVal(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="addfilter-default" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="No default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default</SelectItem>
                  {DATE_DEFAULTS.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid="addfilter-default" value={defVal} onChange={(e) => setDefVal(e.target.value)}
                placeholder={type === "multiselect" ? "a, b, c" : type === "numrange" ? "min, max" : "value"} className="mt-1.5 bg-[color:var(--surface)]" />
            )}
            <p className="text-[11px] text-[color:var(--ink-3)] mt-1">Pre-applied whenever the dashboard opens.</p>
          </div>
          <div><Label>Label (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={field || "Filter label"} className="mt-1.5 bg-[color:var(--surface)]" /></div>
          <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-2.5">
            <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Require a selection</Label><Switch data-testid="addfilter-required" checked={required} onCheckedChange={setRequired} /></div>
            <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Lock for viewers (visible, not changeable)</Label><Switch data-testid="addfilter-locked" checked={locked} onCheckedChange={setLocked} /></div>
            <div className="flex items-center justify-between"><Label className="text-xs text-[color:var(--ink-2)]">Hide from viewers (default still applies)</Label><Switch data-testid="addfilter-hidden" checked={hidden} onCheckedChange={setHidden} /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="addfilter-submit" onClick={submit} className="bg-[color:var(--brand-ocean)] text-white">Add filter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
